import React, { useState } from "react";
import { Slide, Zoom, Fade } from "react-awesome-reveal";
import {
  TbBuildingFactory2,
  TbCpu,
  TbBox,
  TbCode,
  TbBrain,
} from "react-icons/tb";
import {
  Gear,
  Award,
  People,
  ClockHistory,
  ExclamationTriangle,
  Trash,
} from "react-bootstrap-icons";
import RobotBase from "../../assets/img/robot_base.png";
import RobotArm  from "../../assets/img/robot_arm.png";
import "../../styles/EcosistemaSection.css";

/* ─────────────────────────────────────────────
   WhatsApp helper
───────────────────────────────────────────── */
const WA_NUMBER = "5493512035657";
const wa = (msg) =>
  `https://wa.me/${WA_NUMBER}?text=${encodeURIComponent(msg)}`;

/* ─────────────────────────────────────────────
   DATA
───────────────────────────────────────────── */
const solutions = [
  {
    id: "erp",
    icon: <TbBuildingFactory2 size={36} />,
    tag: "ERP Industrial",
    title: "Unificá finanzas, logística, RRHH y operaciones en un solo sistema.",
    modules: [
      "Logística",
      "Finanzas",
      "Calidad",
      "Administración de Personal",
      "Operation Meeting",
    ],
    keyMessage: null,
    ctaHref: wa("Hola! Me interesa conocer más sobre el ERP Industrial de Twinexa. ¿Podemos agendar una demo?"),
    btnLabel: "Solicitar Demo ERP",
    accent: "#7c3aed",
  },
  {
    id: "mes",
    icon: <TbCpu size={36} />,
    tag: "MES – Manufacturing Execution System",
    title: "Control total de producción en tiempo real.",
    modules: [
      "Trazabilidad completa",
      "KPI's de producción en tiempo real",
      "Alertas automáticas",
      "Control de scrap y downtime",
    ],
    keyMessage: null,
    ctaHref: wa("Hola! Me interesa conocer más sobre el MES de Twinexa. ¿Podemos agendar una demo?"),
    btnLabel: "Solicitar Demo MES",
    accent: "#2563eb",
  },
  {
    id: "dt",
    icon: <TbBox size={36} />,
    tag: "Gemelo Digital",
    title: "Simulá tu planta antes de hacer cambios reales.",
    modules: [
      "Mapa virtual de la planta industrial",
      "Simulación de mejoras",
      "Identificación de cuellos de botella",
      "Escenarios predictivos",
    ],
    keyMessage: null,
    ctaHref: wa("Hola! Me interesa conocer más sobre el Gemelo Digital de Twinexa. ¿Podemos hablar?"),
    btnLabel: "Solicitar Análisis de Gemelo Digital",
    accent: "#0891b2",
  },
  {
    id: "ai",
    icon: <TbBrain size={36} />,
    tag: "IA Integrada para Empresas",
    title: "Tu operación hablando con datos.",
    modules: [
      "Generación automática de reportes",
      "Documentación técnica",
      "Análisis de métricas",
      "Agente conectado a tu ERP/MES",
    ],
    keyMessage: "No es un chatbot. Es un copiloto operativo.",
    ctaHref: wa("Hola! Me interesa conocer más sobre la IA integrada para empresas de Twinexa."),
    btnLabel: "Conocer más sobre IA",
    accent: "#d97706",
  },
  {
    id: "dev",
    icon: <TbCode size={36} />,
    tag: "Desarrollo de Software a Medida",
    title: "Para empresas que necesitan algo que ningún sistema estándar resuelve.",
    modules: [
      "Sistemas comerciales",
      "Plataformas internas",
      "Apps móviles",
      "Integraciones complejas",
    ],
    keyMessage: "Si no existe, lo construimos.",
    ctaHref: wa("Hola! Me interesa hablar con un experto de Twinexa sobre desarrollo de software a medida."),
    btnLabel: "Hablar con un experto",
    accent: "#059669",
  }
];

const indicators = [
  {
    name: "OEE",
    icon: <Gear size={28} />,
    full: "Overall Equipment Effectiveness",
    desc: "Mide la eficiencia global de tus equipos combinando disponibilidad, rendimiento y calidad. Twinexa lo calcula automáticamente en tiempo real.",
  },
  {
    name: "FTQ",
    icon: <Award size={28} />,
    full: "First Time Quality",
    desc: "Porcentaje de piezas producidas correctamente a la primera. Con MES, cada defecto queda trazado y el índice se actualiza al instante.",
  },
  {
    name: "DLE",
    icon: <People size={28} />,
    full: "Direct Labour Efficiency",
    desc: "Eficiencia real del trabajo directo vs. el tiempo estándar planificado. Identificá brechas de productividad sin depender de planillas.",
  },
  {
    name: "MTBF",
    icon: <ClockHistory size={28} />,
    full: "Mean Time Between Failures",
    desc: "Tiempo promedio entre fallas de un equipo. Twinexa registra cada paro y calcula el MTBF para orientar el mantenimiento preventivo.",
  },
  {
    name: "Scrap Rate",
    icon: <Trash size={28} />,
    full: "Tasa de Desperdicio",
    desc: "Proporción de material descartado respecto al total producido. Automatizamos su registro y te mostramos las causas raíz en segundos.",
  },
  {
    name: "Downtime",
    icon: <ExclamationTriangle size={28} />,
    full: "Tiempo de Paro",
    desc: "Tiempo en que una línea o máquina no produce. El sistema categoriza cada paro automáticamente para que actúes antes de que el impacto escale.",
  },
];

/* ─────────────────────────────────────────────
   SUB-COMPONENTS
───────────────────────────────────────────── */
const CheckIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    style={{ flexShrink: 0, marginTop: "2px" }}
  >
    <circle cx="8" cy="8" r="8" fill="rgba(162,89,247,0.18)" />
    <path
      d="M5 8.5l2 2 4-4"
      stroke="#a259f7"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/* ── Robot mascot for the AI card ── */
const RobotWave = () => (
  <div className="robot-wrapper" aria-hidden="true">
    {/* Body – floats up/down on hover via parent .eco-solution-card:hover */}
    <div className="robot-body-wrap">
      <img src={RobotBase} alt="" className="robot-base" draggable={false} />
      {/* Arm – rotates from shoulder on hover */}
      <img src={RobotArm}  alt="" className="robot-arm"  draggable={false} />
    </div>
  </div>
);

const SolutionCard = ({ sol, index }) => (
  <Zoom delay={200 + index * 100} duration={600} triggerOnce>
    <div id={`sol-${sol.id}`} className="eco-solution-card" style={{ "--accent": sol.accent }}>
      {/* Header */}
      <div className="eco-card-header">
        <div className="eco-card-icon" style={{ color: sol.accent }}>
          {sol.icon}
        </div>
        <span className="eco-card-tag">{sol.tag}</span>
      </div>

      {/* Title */}
      <p className="eco-card-title">{sol.title}</p>

      {/* Modules */}
      <ul className="eco-card-modules">
        {sol.modules.map((m, i) => (
          <li key={i}>
            <CheckIcon />
            <span>{m}</span>
          </li>
        ))}
      </ul>

      {/* Key message */}
      {sol.keyMessage && (
        <p className="eco-card-key-msg">"{sol.keyMessage}"</p>
      )}

      {/* Robot mascot – AI card only */}
      {sol.id === "ai" && <RobotWave />}

      {/* CTA */}
      <a href={sol.ctaHref} target="_blank" rel="noopener noreferrer" className="eco-card-btn btn btn-connect mt-auto">
        {sol.btnLabel} →
      </a>
    </div>
  </Zoom>
);

const IndicatorCard = ({ ind, index }) => {
  const [flipped, setFlipped] = useState(false);

  return (
    <Zoom delay={150 + index * 100} duration={600} triggerOnce>
      <div
        className={`eco-indicator-card${flipped ? " flipped" : ""}`}
        onClick={() => setFlipped((f) => !f)}
      >
        {/* Front */}
        <div className="eco-ind-front">
          <div className="eco-ind-icon">{ind.icon}</div>
          <h3 className="eco-ind-name">{ind.name}</h3>
          <p className="eco-ind-full">{ind.full}</p>
          <span className="eco-ind-hint">Ver más →</span>
        </div>

        {/* Back */}
        <div className="eco-ind-back">
          <h3 className="eco-ind-name">{ind.name}</h3>
          <p className="eco-ind-desc">{ind.desc}</p>
          <span className="eco-ind-hint">← Volver</span>
        </div>
      </div>
    </Zoom>
  );
};

/* ─────────────────────────────────────────────
   MAIN COMPONENT
───────────────────────────────────────────── */
const EcosistemaSection = () => {
  return (
    <section className="eco-section" id="ecosistema">
      {/* ── SOLUTIONS ECOSYSTEM ── */}
      <div className="container py-5">
        {/* Section header */}
        <Slide direction="up" duration={700} triggerOnce>
          <div className="text-center mb-5">
            <span className="eco-badge mb-4">Ecosistema Twinexa</span>
              {/* Headline */}
                 <Fade direction="up" triggerOnce duration={800} delay={100}>
                   <h2 className="eco-main-title text-center text-white">
                     Una arquitectura.{" "}
                     <span className="highlight">Múltiples soluciones.</span>
                   </h2>
                 </Fade>
           
                 <Fade direction="up" triggerOnce duration={700} delay={200}>
                   <p className="eco-main-sub text-center text-white">
                     Cada módulo opera de forma independiente o integrado con los demás.
                     Escalás sin rehacer.
                   </p>
                 </Fade>
          </div>
        </Slide>

        {/* Cards grid */}
        <div className="eco-solutions-grid">
          {solutions.map((sol, i) => (
            <SolutionCard key={sol.id} sol={sol} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default EcosistemaSection;
